import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { TreeItem, TreeView } from '@mui/lab';
import { userListService } from '../services/userListService';
import { IUserTreeNode, IUserTreeNodeWithChildren } from '../types/userList';

type DependencyRole = 'primary' | 'secondary' | 'third' | 'fourth' | 'fifth' | '';

interface DependencyDraft {
  fullName: string;
  email: string;
  mobileNumber: string;
  dependencyRole: DependencyRole;
}

const dependencyRoleOptions = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'third', label: 'Third' },
  { value: 'fourth', label: 'Fourth' },
  { value: 'fifth', label: 'Fifth' }
];

const dependencyLimit = 5;

const buildTree = (nodes: IUserTreeNode[]): IUserTreeNodeWithChildren[] => {
  const map = new Map<string, IUserTreeNodeWithChildren>();
  nodes.forEach((node) => {
    const nodeId = node._id.toString();
    const parentId = node.parentId ? node.parentId.toString() : null;
    map.set(nodeId, {
      ...node,
      _id: nodeId,
      parentId,
      children: []
    });
  });

  const roots: IUserTreeNodeWithChildren[] = [];
  map.forEach((node) => {
    const parentId = node.parentId ? node.parentId.toString() : null;
    if (parentId && map.has(parentId)) {
      map.get(parentId)?.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const collectSubtreeNodes = (rootId: string, nodes: IUserTreeNode[]): IUserTreeNode[] => {
  const nodeMap = new Map(nodes.map((node) => [node._id.toString(), node]));
  const childrenMap = new Map<string, string[]>();

  nodes.forEach((node) => {
    if (!node.parentId) return;
    const parentId = node.parentId.toString();
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }
    childrenMap.get(parentId)?.push(node._id.toString());
  });

  const result: IUserTreeNode[] = [];
  const visited = new Set<string>();
  const stack = [rootId.toString()];

  while (stack.length) {
    const currentId = stack.pop();
    if (!currentId || visited.has(currentId)) continue;

    const currentNode = nodeMap.get(currentId);
    if (!currentNode) continue;

    result.push(currentNode);
    visited.add(currentId);

    const children = childrenMap.get(currentId) || [];
    for (let i = children.length - 1; i >= 0; i--) {
      stack.push(children[i]);
    }
  }
  return result;
};

const hasContactValue = (contact: DependencyDraft) =>
  Boolean(contact.fullName.trim() || contact.email.trim() || contact.mobileNumber.trim());

const getRoleChipProps = (role?: DependencyRole | null) => {
  if (!role) return null;
  if (role === 'primary') return { label: 'Primary', color: 'primary' as const };
  if (role === 'secondary') return { label: 'Secondary', color: 'warning' as const };
  if (role === 'third') return { label: 'Third', color: 'info' as const };
  if (role === 'fourth') return { label: 'Fourth', color: 'success' as const };
  if (role === 'fifth') return { label: 'Fifth', color: 'default' as const };
  return null;
};

const UserLists: React.FC = () => {
  const [listId, setListId] = useState<string | null>(null);
  const [nodes, setNodes] = useState<IUserTreeNode[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<IUserTreeNode | null>(null);
  const [userForm, setUserForm] = useState({
    fullName: '',
    email: '',
    mobileNumber: ''
  });
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});
  const [dependencyDrafts, setDependencyDrafts] = useState<DependencyDraft[]>([]);
  const [dependencyErrors, setDependencyErrors] = useState<Record<number, Record<string, string>>>({});

  const [dependencyDialogOpen, setDependencyDialogOpen] = useState(false);
  const [editingDependency, setEditingDependency] = useState<IUserTreeNode | null>(null);
  const [dependencyForm, setDependencyForm] = useState({
    fullName: '',
    email: '',
    mobileNumber: '',
    parentId: '',
    dependencyRole: '' as DependencyRole
  });
  const [dependencyFormErrors, setDependencyFormErrors] = useState<Record<string, string>>({});
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailNode, setDetailNode] = useState<IUserTreeNode | null>(null);

  useEffect(() => {
    const ensureList = async () => {
      setLoadingList(true);
      try {
        const lists = await userListService.getLists();
        let list = lists[0];
        if (!list) {
          list = await userListService.createList({
            title: 'My Users',
            description: 'Default user list'
          });
        }
        setListId(list._id.toString());
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to initialize user list');
      } finally {
        setLoadingList(false);
      }
    };
    ensureList();
  }, []);

  const refreshNodes = useCallback(async (targetListId = listId) => {
    if (!targetListId) return;
    setLoadingNodes(true);
    try {
      const data = await userListService.getNodes(targetListId);
      const normalizedNodes = data.map((node: IUserTreeNode) => ({
        ...node,
        _id: node._id.toString(),
        parentId: node.parentId ? node.parentId.toString() : null,
        listId: node.listId.toString()
      }));
      setNodes(normalizedNodes);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load contacts');
    } finally {
      setLoadingNodes(false);
    }
  }, [listId]);

  useEffect(() => {
    if (!listId) {
      setNodes([]);
      return;
    }
    refreshNodes(listId);
  }, [listId, refreshNodes]);

  const rootUsers = useMemo(() => nodes.filter((node) => !node.parentId), [nodes]);

  useEffect(() => {
    if (rootUsers.length === 0) {
      setSelectedUserId(null);
      return;
    }
    if (!selectedUserId || !rootUsers.some((user) => user._id === selectedUserId)) {
      setSelectedUserId(rootUsers[0]._id);
    }
  }, [rootUsers, selectedUserId]);

  const treeNodes = useMemo(() => buildTree(nodes), [nodes]);
  const selectedUser = useMemo(
    () => nodes.find((node) => node._id.toString() === selectedUserId?.toString()) || null,
    [nodes, selectedUserId]
  );
  const selectedTree = useMemo(
    () => treeNodes.find((node) => node._id.toString() === selectedUserId?.toString()) || null,
    [treeNodes, selectedUserId]
  );

  const subtreeNodes = useMemo(() => {
    if (!selectedUserId) return [];
    return collectSubtreeNodes(selectedUserId, nodes);
  }, [selectedUserId, nodes]);

  const dependencyNodes = useMemo(() => {
    if (!selectedUserId) return [];
    return nodes.filter((node) => node.parentId === selectedUserId);
  }, [selectedUserId, nodes]);

  const getChildCount = useCallback(
    (parentId: string, excludeNodeId?: string) =>
      nodes.filter((node) => node.parentId === parentId && node._id !== excludeNodeId).length,
    [nodes]
  );

  const getUsedRolesForParent = useCallback(
    (parentId: string, excludeNodeId?: string) =>
      new Set(
        nodes
          .filter((node) => node.parentId === parentId && node._id !== excludeNodeId)
          .map((node) => node.dependencyRole)
          .filter((role): role is Exclude<DependencyRole, ''> => Boolean(role))
      ),
    [nodes]
  );

  const selectedUserDependencyCount = dependencyNodes.length;

  useEffect(() => {
    if (!selectedUserId) {
      setExpanded([]);
      return;
    }
    const ids = subtreeNodes.map((node) => node._id.toString());
    setExpanded(ids);
  }, [selectedUserId, subtreeNodes]);

  useEffect(() => {
    if (!selectedUserId) {
      setSelectedNodeId(null);
      return;
    }
    setSelectedNodeId(selectedUserId);
  }, [selectedUserId]);

  const openAddUserDialog = () => {
    setEditingUser(null);
    setUserForm({ fullName: '', email: '', mobileNumber: '' });
    setDependencyDrafts([]);
    setUserErrors({});
    setDependencyErrors({});
    setUserDialogOpen(true);
  };

  const openEditUserDialog = (user: IUserTreeNode) => {
    setEditingUser(user);
    setUserForm({
      fullName: user.fullName,
      email: user.email,
      mobileNumber: user.mobileNumber
    });
    setDependencyDrafts([]);
    setUserErrors({});
    setDependencyErrors({});
    setUserDialogOpen(true);
  };

  const validateUserForm = () => {
    const errors: Record<string, string> = {};
    if (!userForm.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    if (!userForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(userForm.email)) {
      errors.email = 'Email is invalid';
    }
    if (!userForm.mobileNumber.trim()) {
      errors.mobileNumber = 'Mobile number is required';
    }
    setUserErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateDependencyDrafts = () => {
    const errors: Record<number, Record<string, string>> = {};
    const usedRoles = new Set<DependencyRole>();
    dependencyDrafts.forEach((dependency, index) => {
      if (!hasContactValue(dependency)) return;
      const rowErrors: Record<string, string> = {};
      if (!dependency.fullName.trim()) {
        rowErrors.fullName = 'Full name is required';
      }
      if (!dependency.email.trim()) {
        rowErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(dependency.email)) {
        rowErrors.email = 'Email is invalid';
      }
      if (!dependency.mobileNumber.trim()) {
        rowErrors.mobileNumber = 'Mobile number is required';
      }
      if (dependency.dependencyRole) {
        if (usedRoles.has(dependency.dependencyRole)) {
          rowErrors.dependencyRole = 'This role is already selected.';
        } else {
          usedRoles.add(dependency.dependencyRole);
        }
      }
      if (Object.keys(rowErrors).length > 0) {
        errors[index] = rowErrors;
      }
    });
    setDependencyErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveUser = async () => {
    if (!listId) return;
    if (!validateUserForm()) return;
    if (!editingUser && !validateDependencyDrafts()) return;

    const dependenciesToCreate = editingUser ? [] : dependencyDrafts.filter(hasContactValue);
    if (!editingUser && dependenciesToCreate.length > dependencyLimit) {
      setError(`Only ${dependencyLimit} dependencies are allowed.`);
      return;
    }

    try {
      if (editingUser) {
        await userListService.updateNode(listId, editingUser._id, {
          fullName: userForm.fullName.trim(),
          email: userForm.email.trim(),
          mobileNumber: userForm.mobileNumber.trim()
        });
      } else {
        const createdUser = await userListService.createNode(listId, {
          fullName: userForm.fullName.trim(),
          email: userForm.email.trim(),
          mobileNumber: userForm.mobileNumber.trim(),
          parentId: null
        });
        setSelectedUserId(createdUser._id.toString());

        if (dependenciesToCreate.length > 0) {
          await Promise.all(
            dependenciesToCreate.map((dependency) =>
              userListService.createNode(listId, {
                fullName: dependency.fullName.trim(),
                email: dependency.email.trim(),
                mobileNumber: dependency.mobileNumber.trim(),
                parentId: createdUser._id,
                dependencyRole: dependency.dependencyRole || null
              })
            )
          );
        }
      }

      setUserDialogOpen(false);
      setEditingUser(null);
      await refreshNodes();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const deleteUser = async () => {
    if (!listId || !selectedUser) return;
    if (!window.confirm('Delete this user and all dependency contacts?')) return;
    try {
      await userListService.deleteNode(listId, selectedUser._id);
      await refreshNodes();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const openDependencyDialog = (node?: IUserTreeNode, parentId?: string) => {
    if (node) {
      setEditingDependency(node);
      setDependencyForm({
        fullName: node.fullName,
        email: node.email,
        mobileNumber: node.mobileNumber,
        parentId: node.parentId || '',
        dependencyRole: (node.dependencyRole || '') as DependencyRole
      });
    } else {
      setEditingDependency(null);
      const targetParentId = parentId || selectedUserId || '';
      const usedRoles = targetParentId ? getUsedRolesForParent(targetParentId) : new Set<DependencyRole>();
      const nextRole =
        dependencyRoleOptions.find((option) => !usedRoles.has(option.value as DependencyRole))?.value || '';
      setDependencyForm({
        fullName: '',
        email: '',
        mobileNumber: '',
        parentId: targetParentId,
        dependencyRole: nextRole as DependencyRole
      });
    }
    setDependencyFormErrors({});
    setDependencyDialogOpen(true);
  };

  const validateDependencyForm = () => {
    const errors: Record<string, string> = {};
    if (!dependencyForm.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    if (!dependencyForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(dependencyForm.email)) {
      errors.email = 'Email is invalid';
    }
    if (!dependencyForm.mobileNumber.trim()) {
      errors.mobileNumber = 'Mobile number is required';
    }
    const parentId = dependencyForm.parentId || selectedUserId || '';
    if (dependencyForm.dependencyRole && parentId) {
      const usedRoles = getUsedRolesForParent(parentId, editingDependency?._id);
      if (usedRoles.has(dependencyForm.dependencyRole)) {
        errors.dependencyRole = 'This role is already assigned.';
      }
    }
    setDependencyFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveDependency = async () => {
    if (!listId) return;
    if (!validateDependencyForm()) return;

    const parentId = dependencyForm.parentId || selectedUserId || null;
    if (!parentId) {
      setDependencyFormErrors((prev) => ({
        ...prev,
        parentId: 'Select a parent contact'
      }));
      return;
    }

    const siblingCount = getChildCount(parentId, editingDependency?._id);
    if (siblingCount >= dependencyLimit) {
      setDependencyFormErrors((prev) => ({
        ...prev,
        parentId: `Only ${dependencyLimit} dependencies are allowed.`
      }));
      return;
    }

    try {
      const payload = {
        fullName: dependencyForm.fullName.trim(),
        email: dependencyForm.email.trim(),
        mobileNumber: dependencyForm.mobileNumber.trim(),
        parentId,
        dependencyRole: dependencyForm.dependencyRole || null
      };

      if (editingDependency) {
        await userListService.updateNode(listId, editingDependency._id, payload);
      } else {
        await userListService.createNode(listId, payload);
      }

      setDependencyDialogOpen(false);
      setEditingDependency(null);
      await refreshNodes();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save dependency');
    }
  };

  const deleteDependency = async (nodeId: string) => {
    if (!listId) return;
    if (!window.confirm('Delete this dependency and all attached contacts?')) return;
    try {
      await userListService.deleteNode(listId, nodeId);
      await refreshNodes();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete dependency');
    }
  };

  const parentOptions = useMemo(() => {
    if (!selectedUserId) return [];
    const invalidIds = editingDependency
      ? new Set(collectSubtreeNodes(editingDependency._id, nodes).map((node) => node._id))
      : new Set<string>();
    return subtreeNodes.filter((node) => {
      if (invalidIds.has(node._id)) return false;
      const count = getChildCount(node._id, editingDependency?._id);
      return count < dependencyLimit;
    });
  }, [selectedUserId, editingDependency, subtreeNodes, nodes, getChildCount]);

  const dependencyRoleUsed = useMemo(() => {
    const parentId = dependencyForm.parentId || selectedUserId || '';
    if (!parentId) return new Set<DependencyRole>();
    return getUsedRolesForParent(parentId, editingDependency?._id);
  }, [dependencyForm.parentId, selectedUserId, editingDependency, getUsedRolesForParent]);

  const openDetailDialog = (node: IUserTreeNode) => {
    setDetailNode(node);
    setDetailDialogOpen(true);
  };

  const renderTree = (node: IUserTreeNodeWithChildren, isRoot = false) => {
    if (!node) return null;
    const nodeId = node._id.toString();
    const chipProps = getRoleChipProps(node.dependencyRole);
    const isDependencyLimitReached = getChildCount(nodeId) >= dependencyLimit;

    return (
      <TreeItem
        key={nodeId}
        nodeId={nodeId}
        label={
          <Box
            onClick={(e) => {
              e.stopPropagation();
              openDetailDialog(node);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1,
              px: 1.5,
              my: 0.5,
              borderRadius: 2,
              transition: 'all 0.2s',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                '& .actions-box': { opacity: 1 }
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  {node.fullName}
                </Typography>
                {isRoot ? (
                  <Chip size="small" variant="filled" label="User" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }} />
                ) : (
                  chipProps && (
                    <Chip
                      size="small"
                      variant="filled"
                      {...chipProps}
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {node.email}
              </Typography>
            </Box>
            <Box
              className="actions-box"
              sx={{
                display: 'flex',
                gap: 0.5,
                opacity: 0,
                transition: 'opacity 0.2s'
              }}
            >
              <IconButton
                size="small"
                disabled={isDependencyLimitReached}
                onClick={(event) => {
                  event.stopPropagation();
                  openDependencyDialog(undefined, nodeId);
                }}
                sx={{ color: isDependencyLimitReached ? 'text.disabled' : '#1976d2' }}
              >
                <PersonAddIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  openEditUserDialog(node);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              {!isRoot && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation();
                    deleteDependency(nodeId);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        }
      >
        {Array.isArray(node.children) && node.children.map((child) => renderTree(child))}
      </TreeItem>
    );
  };

  if (loadingList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.5px',
              mb: 0.5
            }}
          >
            User Contacts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Manage your users and their hierarchical dependencies efficiently.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddUserDialog}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.2,
            background: 'linear-gradient(45deg, #1976d2 30%, #21cbf3 90%)',
            boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            }
          }}
        >
          Add New User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1a237e' }}>
                Primary Users
              </Typography>
              {loadingNodes ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress size={32} thickness={5} />
                </Box>
              ) : rootUsers.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">No users yet.</Typography>
                  <Typography variant="caption">Add your first user to start.</Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {rootUsers.map((user) => (
                    <ListItemButton
                      key={user._id}
                      selected={user._id === selectedUserId}
                      onClick={() => setSelectedUserId(user._id)}
                      sx={{
                        borderRadius: 3,
                        mb: 1.5,
                        py: 1.5,
                        px: 2,
                        transition: 'all 0.2s',
                        '&.Mui-selected': {
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                          borderLeft: '4px solid #1976d2',
                          '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' }
                        },
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.02)'
                        }
                      }}
                    >
                      <ListItemText
                        primary={user.fullName}
                        secondary={user.email}
                        primaryTypographyProps={{ fontWeight: 700, color: '#2c3e50', fontSize: '0.95rem' }}
                        secondaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetailDialog(user);
                        }}
                        sx={{ ml: 1, color: '#1976d2', opacity: 0.5, '&:hover': { opacity: 1 } }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                      <ChevronRightIcon sx={{ opacity: 0.3 }} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.05)', minHeight: 500 }}>
            <CardContent sx={{ p: 4 }}>
              {selectedUser ? (
                <Stack spacing={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a237e', mb: 0.5 }}>
                        {selectedUser.fullName}
                      </Typography>
                      <Stack direction="row" spacing={2} sx={{ color: 'text.secondary' }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <strong>Email:</strong> {selectedUser.email}
                        </Typography>
                        <Divider orientation="vertical" flexItem />
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <strong>Mobile:</strong> {selectedUser.mobileNumber}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1.5}>
                      <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => openDependencyDialog()}
                        disabled={selectedUserDependencyCount >= dependencyLimit}
                        sx={{
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                          color: '#1976d2',
                          fontWeight: 600,
                          boxShadow: 'none',
                          '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.15)', boxShadow: 'none' }
                        }}
                      >
                        Add Dependency
                      </Button>
                      <IconButton
                        onClick={() => openDetailDialog(selectedUser)}
                        sx={{ bgcolor: 'rgba(25, 118, 210, 0.05)', color: '#1976d2' }}
                      >
                        <InfoIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => openEditUserDialog(selectedUser)}
                        sx={{ bgcolor: 'rgba(0,0,0,0.03)' }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={deleteUser}
                        sx={{ bgcolor: 'rgba(211, 47, 47, 0.05)' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
                      Dependencies
                      <Chip label={dependencyNodes.length} size="small" sx={{ fontWeight: 700 }} />
                    </Typography>
                    {loadingNodes ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : dependencyNodes.length === 0 ? (
                      <Box sx={{ py: 6, textAlign: 'center', opacity: 0.6 }}>
                        <Typography variant="body2">No dependencies yet.</Typography>
                        <Typography variant="caption">Click "Add Dependency" to build your network.</Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        <List sx={{ p: 0 }}>
                          {dependencyNodes.map((node) => {
                            const chipProps = getRoleChipProps(node.dependencyRole);
                            return (
                              <ListItemButton
                                key={node._id}
                                onClick={() => openDetailDialog(node)}
                                sx={{
                                  borderRadius: 2,
                                  mb: 1,
                                  py: 1.5,
                                  px: 2,
                                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                                  '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' }
                                }}
                              >
                                <ListItemText
                                  primary={node.fullName}
                                  secondary={node.email}
                                  primaryTypographyProps={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.95rem' }}
                                  secondaryTypographyProps={{ fontSize: '0.8rem' }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {chipProps && (
                                    <Chip
                                      size="small"
                                      variant="filled"
                                      {...chipProps}
                                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                    />
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openDependencyDialog(node);
                                    }}
                                    sx={{ color: '#1976d2' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      deleteDependency(node._id);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </ListItemButton>
                            );
                          })}
                        </List>
                        {selectedTree && (
                          <Box sx={{
                            p: 2,
                            border: '1px solid rgba(0,0,0,0.04)',
                            borderRadius: 3,
                            bgcolor: 'rgba(0,0,0,0.01)',
                            maxHeight: '320px',
                            overflowY: 'auto'
                          }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                              Hierarchy View
                            </Typography>
                            <TreeView
                              expanded={expanded}
                              selected={selectedNodeId || ''}
                              onNodeSelect={(_: React.SyntheticEvent, nodeId: string | string[]) => {
                                const nextId = Array.isArray(nodeId) ? nodeId[0] ?? null : nodeId;
                                setSelectedNodeId(nextId);
                              }}
                              onNodeToggle={(_: React.SyntheticEvent, nodeIds: string[]) => setExpanded(nodeIds)}
                              defaultCollapseIcon={<ExpandMoreIcon sx={{ color: '#1976d2' }} />}
                              defaultExpandIcon={<ChevronRightIcon sx={{ color: '#1976d2' }} />}
                              sx={{ flexGrow: 1 }}
                            >
                              {renderTree(selectedTree, true)}
                            </TreeView>
                          </Box>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ py: 15, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>No User Selected</Typography>
                  <Typography variant="body2">Choose a primary user from the list to manage their dependencies.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingUser ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <TextField
                label="Full Name"
                fullWidth
                value={userForm.fullName}
                onChange={(event) => setUserForm({ ...userForm, fullName: event.target.value })}
                error={!!userErrors.fullName}
                helperText={userErrors.fullName}
                required
              />
              <TextField
                label="Email"
                fullWidth
                value={userForm.email}
                onChange={(event) => setUserForm({ ...userForm, email: event.target.value })}
                error={!!userErrors.email}
                helperText={userErrors.email}
                required
              />
              <TextField
                label="Mobile Number"
                fullWidth
                value={userForm.mobileNumber}
                onChange={(event) => setUserForm({ ...userForm, mobileNumber: event.target.value })}
                error={!!userErrors.mobileNumber}
                helperText={userErrors.mobileNumber}
                required
              />
            </Stack>

            {!editingUser && (
              <>
                <Divider />
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Dependency Contacts
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      disabled={dependencyDrafts.length >= dependencyLimit}
                      onClick={() => {
                        const usedRoles = new Set(
                          dependencyDrafts
                            .map((dependency) => dependency.dependencyRole)
                            .filter((role): role is DependencyRole => Boolean(role))
                        );
                        const nextRole =
                          dependencyRoleOptions.find((option) => !usedRoles.has(option.value as DependencyRole))
                            ?.value || '';
                        setDependencyDrafts((prev) => [
                          ...prev,
                          {
                            fullName: '',
                            email: '',
                            mobileNumber: '',
                            dependencyRole: nextRole as DependencyRole
                          }
                        ]);
                      }}
                    >
                      Add Dependency
                    </Button>
                  </Box>

                  {dependencyDrafts.length === 0 ? (
                    <Box sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                      Add dependency contacts now or later from the user details.
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {dependencyDrafts.map((dependency, index) => {
                        const usedDraftRoles = new Set(
                          dependencyDrafts
                            .filter((_, idx) => idx !== index)
                            .map((item) => item.dependencyRole)
                            .filter((role): role is DependencyRole => Boolean(role))
                        );
                        return (
                          <Box
                            key={`dependency-${index}`}
                            sx={{
                              border: '1px solid #e0e0e0',
                              borderRadius: 2,
                              p: 2,
                              backgroundColor: 'rgba(0,0,0,0.02)'
                            }}
                          >
                            <Stack spacing={2}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Dependency {index + 1}
                                </Typography>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    setDependencyDrafts((prev) => prev.filter((_, idx) => idx !== index))
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <TextField
                                label="Full Name"
                                fullWidth
                                value={dependency.fullName}
                                onChange={(event) =>
                                  setDependencyDrafts((prev) =>
                                    prev.map((item, idx) =>
                                      idx === index ? { ...item, fullName: event.target.value } : item
                                    )
                                  )
                                }
                                error={!!dependencyErrors[index]?.fullName}
                                helperText={dependencyErrors[index]?.fullName}
                                required
                              />
                              <TextField
                                label="Email"
                                fullWidth
                                value={dependency.email}
                                onChange={(event) =>
                                  setDependencyDrafts((prev) =>
                                    prev.map((item, idx) =>
                                      idx === index ? { ...item, email: event.target.value } : item
                                    )
                                  )
                                }
                                error={!!dependencyErrors[index]?.email}
                                helperText={dependencyErrors[index]?.email}
                                required
                              />
                              <TextField
                                label="Mobile Number"
                                fullWidth
                                value={dependency.mobileNumber}
                                onChange={(event) =>
                                  setDependencyDrafts((prev) =>
                                    prev.map((item, idx) =>
                                      idx === index ? { ...item, mobileNumber: event.target.value } : item
                                    )
                                  )
                                }
                                error={!!dependencyErrors[index]?.mobileNumber}
                                helperText={dependencyErrors[index]?.mobileNumber}
                                required
                              />
                              <TextField
                                select
                                label="Dependency Type"
                                fullWidth
                                value={dependency.dependencyRole}
                                onChange={(event) =>
                                  setDependencyDrafts((prev) =>
                                    prev.map((item, idx) =>
                                      idx === index
                                        ? { ...item, dependencyRole: event.target.value as DependencyRole }
                                        : item
                                    )
                                  )
                                }
                                error={!!dependencyErrors[index]?.dependencyRole}
                                helperText={
                                  dependencyErrors[index]?.dependencyRole
                                  || 'Optional: set visibility label for this dependency.'
                                }
                              >
                                <MenuItem value="">Unassigned</MenuItem>
                                {dependencyRoleOptions.map((option) => {
                                  const isUsed = usedDraftRoles.has(option.value as DependencyRole);
                                  const isSelected = dependency.dependencyRole === option.value;
                                  return (
                                    <MenuItem key={option.value} value={option.value} disabled={isUsed && !isSelected}>
                                      {option.label}
                                    </MenuItem>
                                  );
                                })}
                              </TextField>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveUser}>
            {editingUser ? 'Update User' : 'Save User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dependencyDialogOpen} onClose={() => setDependencyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingDependency ? 'Edit Dependency' : 'Add Dependency'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Full Name"
              fullWidth
              value={dependencyForm.fullName}
              onChange={(event) => setDependencyForm({ ...dependencyForm, fullName: event.target.value })}
              error={!!dependencyFormErrors.fullName}
              helperText={dependencyFormErrors.fullName}
              required
            />
            <TextField
              label="Email"
              fullWidth
              value={dependencyForm.email}
              onChange={(event) => setDependencyForm({ ...dependencyForm, email: event.target.value })}
              error={!!dependencyFormErrors.email}
              helperText={dependencyFormErrors.email}
              required
            />
            <TextField
              label="Mobile Number"
              fullWidth
              value={dependencyForm.mobileNumber}
              onChange={(event) => setDependencyForm({ ...dependencyForm, mobileNumber: event.target.value })}
              error={!!dependencyFormErrors.mobileNumber}
              helperText={dependencyFormErrors.mobileNumber}
              required
            />
            <TextField
              select
              label="Attach To"
              fullWidth
              value={dependencyForm.parentId}
              onChange={(event) => setDependencyForm({ ...dependencyForm, parentId: event.target.value })}
              helperText={dependencyFormErrors.parentId || 'Choose a parent contact to attach under.'}
              error={!!dependencyFormErrors.parentId}
            >
              {parentOptions.map((node) => (
                <MenuItem key={node._id} value={node._id}>
                  {node.fullName} ({node.email})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Dependency Type"
              fullWidth
              value={dependencyForm.dependencyRole}
              onChange={(event) =>
                setDependencyForm({ ...dependencyForm, dependencyRole: event.target.value as DependencyRole })
              }
              error={!!dependencyFormErrors.dependencyRole}
              helperText={dependencyFormErrors.dependencyRole || 'Optional: set visibility label for this dependency.'}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {dependencyRoleOptions.map((option) => {
                const isUsed = dependencyRoleUsed.has(option.value as DependencyRole);
                const isSelected = dependencyForm.dependencyRole === option.value;
                return (
                  <MenuItem key={option.value} value={option.value} disabled={isUsed && !isSelected}>
                    {option.label}
                  </MenuItem>
                );
              })}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDependencyDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveDependency}>
            {editingDependency ? 'Update Dependency' : 'Add Dependency'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1, color: '#1a237e' }}>Contact Details</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ py: 2 }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Full Name
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  {detailNode?.fullName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Email Address
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailNode?.email}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Mobile Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailNode?.mobileNumber}
                </Typography>
              </Box>

              {detailNode?.dependencyRole && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Dependency Type
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip {...getRoleChipProps(detailNode.dependencyRole)} sx={{ fontWeight: 600 }} />
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)} variant="contained" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default UserLists;
